import { useMemo, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  getSiteSnapshot,
  pickSnapshotScreenshot,
  refreshSiteSnapshot,
} from "@/lib/api/onboardingTests"

/** The crawl can lag the plan, so the poll keeps asking for a while, then stops. */
const SCREENSHOT_POLL_MS = 5000
const SCREENSHOT_POLL_CEILING_MS = 60000

/**
 * The site snapshot the plan shows as the control: polls until a screenshot
 * lands or the ceiling passes. Shared by the reading view and the plan cards,
 * which use the same query key so they read one cache.
 */
export function useOnboardingSnapshot({
  propertyId,
  token,
  domain,
  enabled = true,
}: {
  propertyId?: string | null
  token?: string | null
  domain?: string | null
  enabled?: boolean
}): string | null {
  const pollStartedAt = useRef(Date.now())
  const capturedOnce = useRef(false)

  const snapshotQuery = useQuery({
    queryKey: ["onboarding-plan-snapshot", propertyId],
    enabled: Boolean(propertyId && token && enabled),
    retry: false,
    queryFn: async () => {
      // First pass also asks the backend to capture anything still missing a screenshot.
      const snapshot = capturedOnce.current
        ? await getSiteSnapshot(propertyId as string, token as string)
        : await refreshSiteSnapshot(propertyId as string, token as string)
      capturedOnce.current = true
      return snapshot
    },
    refetchInterval: (query) => {
      if (query.state.status === "error") return false
      if (pickSnapshotScreenshot(query.state.data)) return false
      if (Date.now() - pollStartedAt.current > SCREENSHOT_POLL_CEILING_MS) return false
      return SCREENSHOT_POLL_MS
    },
  })

  return useMemo(
    () => pickSnapshotScreenshot(snapshotQuery.data, domain),
    [snapshotQuery.data, domain],
  )
}
